import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

// Types

/** Individual cost line item from VAPI (provider-level detail). */
export interface CostItem {
  type: string             // "transcriber" | "model" | "voice" | "vapi" | "analysis" | "knowledge-base"
  cost: number
  subType?: string         // e.g. "normal" for vapi
  analysisType?: string    // e.g. "summary", "successEvaluation"
  minutes?: number
  characters?: number
  promptTokens?: number
  completionTokens?: number
  transcriber?: { provider: string; model: string }
  model?: { provider: string; model: string }
  voice?: { provider: string; voiceId?: string; model?: string }
}

/** Summary breakdown + detailed items from VAPI costBreakdown + costs.
 *
 * The ``vapi`` and ``retell`` keys are platform-fee categories — VAPI
 * splits cost into stt/llm/tts/vapi, while Retell currently bills as a
 * single combined per-minute line item under ``retell`` (so the other
 * three are typically zero/absent for Retell calls). */
export interface CostBreakdown {
  breakdown: {
    stt?: number
    llm?: number
    tts?: number
    vapi?: number
    retell?: number
    total?: number
    llmPromptTokens?: number
    llmCompletionTokens?: number
    ttsCharacters?: number
    analysisCostBreakdown?: Record<string, number>
  }
  items: CostItem[]
}

export type CallOutcome =
  | 'interested'
  | 'callback_requested'
  | 'not_interested'
  | 'do_not_call'
  | 'voicemail'
  | 'no_answer'
  | 'wrong_number'

export type InboundReason =
  | 'schedule_appointment'
  | 'ask_question'
  | 'complaint'
  | 'transfer_request'
  | 'other'

export type ProposalStatus = 'pending' | 'approved' | 'dismissed'

export interface Call {
  call_sid: string
  direction: string
  mode: string
  status: string
  from_number: string
  to_number: string
  started_at: number
  ended_at: number | null
  duration_s: number | null
  ended_by: string | null
  ended_reason: string | null
  recording_url?: string
  call_summary?: string
  /** Customer-facing charge for this call. Null while no usage event has been written yet. */
  price_usd?: number | null
  /** Internal provider cost — only present in superadmin responses. */
  cost_usd?: number
  /** Internal provider cost composition — only present in superadmin responses. */
  cost_breakdown?: CostBreakdown | null
  /** Stable tag identifying *why* this call happened. Outbound calls are
   *  tagged at dial-time from which assistant ran; inbound calls are tagged
   *  post-call from the inbound assistant's ``inbound_reason`` field. */
  purpose?: string | null
  assistant_id_used?: string | null

  // ── Insights ────────────────────────────────────────────────────────
  interest_level?: number | null
  interest_reasoning?: string | null
  outcome?: CallOutcome | null
  inbound_reason?: InboundReason | null

  // ── Proposed contact update ─────────────────────────────────────────
  extracted_first_name?: string | null
  extracted_last_name?: string | null
  extracted_email?: string | null
  extracted_birth_date?: string | null
  contact_update_status?: ProposalStatus | null

  // ── Proposed callback ───────────────────────────────────────────────
  callback_at?: string | null
  callback_reason?: string | null
  callback_confidence?: number | null
  callback_needs_review?: boolean | null
  callback_status?: ProposalStatus | null
  callback_calendar_event_id?: string | null

  // ── Provider-native post-call analysis ──────────────────────────────
  /** Routing-oriented analysis from the inbound Retell receptionist. Present
   *  only on inbound retell calls; null for VAPI/outbound (which use the
   *  lead-gen insight fields above). */
  provider_analysis?: {
    caller_name?: string
    target_requested?: string
    reason_for_call?: string
    follow_up_created?: boolean
    call_outcome?: string
  } | null
}

export interface CallEvent {
  id: number
  call_sid: string
  event_type: string
  timestamp: number
  payload_json: string
}

export interface TranscriptEntry {
  event_type: string
  timestamp: number
  text: string
}

export interface PhoneEntry {
  phone: string
  call_count: number
  last_call_at: number
}

export interface Stats {
  total_calls: number
  active_calls: number
  avg_duration_s: number
  total_tool_invocations: number
}

interface CallsResponse {
  calls: Call[]
  total: number
  limit: number
  offset: number
}

interface CallDetailResponse {
  call: Call
  events: CallEvent[]
}

interface TranscriptResponse {
  call_sid: string
  transcript: TranscriptEntry[]
  summary?: string
  recording_url?: string
}

interface PhonesResponse {
  phones: PhoneEntry[]
}

const BASE = '/telemetry'

/** Sort fields exposed on /telemetry/calls. We deliberately omit `cost_usd`
 *  here even though the backend accepts it — `cost_usd` is superadmin-only and
 *  scrubbed for org users, so sorting by it would order rows by a value those
 *  users can't see. Sorting by `price_usd` lives in `usage_events`, not in
 *  the calls table, so it would need a JOIN; out of scope for this surface. */
export type CallSortField = 'started_at' | 'duration_s'
export type SortOrder = 'asc' | 'desc'

export interface CallListParams {
  limit?: number
  offset?: number
  status?: string
  direction?: string
  outcome?: string
  /** Unix seconds — inclusive lower bound on started_at. */
  started_after?: number
  /** Unix seconds — exclusive upper bound on started_at. */
  started_before?: number
  /** Free-text phone search; non-digit characters are stripped server-side. */
  phone?: string
  /** Outbound: lead_qualification / follow_up / confirmation / csat_followup.
   *  Inbound: schedule_appointment / ask_question / complaint / transfer_request / other. */
  purpose?: string
  sort?: CallSortField
  order?: SortOrder
}

// Hooks
export function useCalls(params?: CallListParams) {
  const searchParams = new URLSearchParams()
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.offset) searchParams.set('offset', String(params.offset))
  if (params?.status) searchParams.set('status', params.status)
  if (params?.direction) searchParams.set('direction', params.direction)
  if (params?.outcome) searchParams.set('outcome', params.outcome)
  if (params?.started_after != null) searchParams.set('started_after', String(params.started_after))
  if (params?.started_before != null) searchParams.set('started_before', String(params.started_before))
  if (params?.phone) searchParams.set('phone', params.phone)
  if (params?.purpose) searchParams.set('purpose', params.purpose)
  if (params?.sort) searchParams.set('sort', params.sort)
  if (params?.order) searchParams.set('order', params.order)
  const qs = searchParams.toString()

  return useQuery({
    queryKey: ['calls', params],
    queryFn: () => apiFetch<CallsResponse>(`${BASE}/calls${qs ? `?${qs}` : ''}`),
    refetchInterval: 15_000,
  })
}

export function useCallDetail(callSid: string | undefined) {
  return useQuery({
    queryKey: ['call', callSid],
    queryFn: () => apiFetch<CallDetailResponse>(`${BASE}/calls/${callSid}`),
    enabled: !!callSid,
  })
}

export function useCallTranscript(callSid: string | undefined) {
  return useQuery({
    queryKey: ['transcript', callSid],
    queryFn: () => apiFetch<TranscriptResponse>(`${BASE}/calls/${callSid}/transcript`),
    enabled: !!callSid,
  })
}

export function useCallsByPhone(phone: string | undefined, params?: { limit?: number; offset?: number }) {
  // The phone travels as a query parameter, never a path segment: uvicorn's
  // access log writes request lines to stdout (retained by Railway outside a
  // BAA) and only the `phone=` query form is caught by the backend's PHI
  // redaction filter.
  const searchParams = new URLSearchParams()
  if (phone) searchParams.set('phone', phone)
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.offset) searchParams.set('offset', String(params.offset))
  const qs = searchParams.toString()

  return useQuery({
    queryKey: ['callsByPhone', phone, params],
    queryFn: () => apiFetch<CallsResponse>(`${BASE}/calls/by-phone?${qs}`),
    enabled: !!phone,
    refetchInterval: 15_000,
  })
}

export function usePhones() {
  return useQuery({
    queryKey: ['phones'],
    queryFn: () => apiFetch<PhonesResponse>(`${BASE}/phones`),
    refetchInterval: 15_000,
  })
}

export function useStats() {
  return useQuery({
    queryKey: ['stats'],
    queryFn: () => apiFetch<Stats>(`${BASE}/stats`),
    refetchInterval: 10_000,
  })
}

// ── Proposal mutations ─────────────────────────────────────────────────

export interface ContactUpdatePayload {
  action: 'approve' | 'dismiss'
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  birth_date?: string | null
}

export interface CallbackApprovalPayload {
  action: 'approve' | 'dismiss'
  /** ISO 8601 datetime when approving. */
  callback_at?: string | null
  callback_reason?: string | null
}

export interface CallbackApprovalResponse {
  call: Call
  calendar_status?: 'created' | 'skipped_no_integration' | 'failed'
  calendar_event_id?: string | null
  calendar_html_link?: string | null
}

export function useApproveContactUpdate(callSid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ContactUpdatePayload) =>
      apiFetch<{ call: Call }>(`/calls/${callSid}/contact-update`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      if (callSid) qc.invalidateQueries({ queryKey: ['call', callSid] })
    },
  })
}

export function useApproveCallback(callSid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CallbackApprovalPayload) =>
      apiFetch<CallbackApprovalResponse>(`/calls/${callSid}/callback`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      if (callSid) qc.invalidateQueries({ queryKey: ['call', callSid] })
    },
  })
}
